import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Swal from 'sweetalert2';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { Button, Input, Select, Switch } from '@/components/ui';
import { CTA } from '@components/ui/designTokens';
import { newCompanyStep1Schema, type NewCompanyStep1FormData } from '../../utils/newCompanySchema';
import type { NewCompanyStep1Data } from '../../types/newCompanyWizard.types';
import { compressImage, LOGO_MAX_INPUT_BYTES } from '@lib/utils/compressImage';

interface Step1CompanyDataProps {
  data: NewCompanyStep1Data;
  isSuperAdmin: boolean;
  onNext: (data: NewCompanyStep1FormData & { logoFile: File | null }) => void;
}

const MAX_LOGO_SIZE = 1 * 1024 * 1024; // 1MB, igual que EditCompanyPage

export function Step1CompanyData({ data, isSuperAdmin, onNext }: Step1CompanyDataProps) {
  const [logoFile, setLogoFile] = useState<File | null>(data.logoFile);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(newCompanyStep1Schema),
    mode: 'onChange',
    defaultValues: {
      fullName: data.fullName,
      name: data.name,
      code: data.code,
      numberId: data.numberId,
      dv: data.dv,
      typeTaxpayer: data.typeTaxpayer,
      phone: data.phone,
      isLimited: data.isLimited,
    },
  });

  const isLimited = watch('isLimited');
  const typeTaxpayer = watch('typeTaxpayer');
  const isJuridico = typeTaxpayer === '2';

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith('image/')) {
      await Swal.fire({
        icon: 'error',
        title: 'Tipo de archivo inválido',
        text: 'El archivo debe ser una imagen',
        confirmButtonColor: '#ef4444',
      });
      e.target.value = '';
      return;
    }

    // Igual que EditCompanyPage: se reduce el peso antes de subir y el límite
    // de 1MB se aplica sobre la imagen ya comprimida.
    const file = selectedFile.size <= LOGO_MAX_INPUT_BYTES
      ? await compressImage(selectedFile).catch(() => selectedFile)
      : selectedFile;

    if (file.size > MAX_LOGO_SIZE) {
      await Swal.fire({
        icon: 'error',
        title: 'Archivo muy grande',
        text: 'La imagen no debe ser mayor a 1MB. Use una imagen más pequeña o en formato PNG/JPG.',
        confirmButtonColor: '#ef4444',
      });
      e.target.value = '';
      return;
    }

    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const onSubmit = (formData: NewCompanyStep1FormData) => {
    if (isLimited && isSuperAdmin) {
      Swal.fire({
        title: 'Consumo ilimitado',
        text: '¿Está seguro de activar tipo de consumo ilimitado?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (result.isConfirmed) {
          onNext({ ...formData, logoFile });
        }
      });
      return;
    }
    onNext({ ...formData, logoFile });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="font-display" data-testid="company-wizard-step1-container">
      <div className="flex flex-col md:flex-row gap-5">
        <div className="flex-shrink-0 flex flex-col items-center gap-3">
          <div className="w-32 h-32 rounded-full border-2 border-dashed border-secondary-300 dark:border-secondary-600 overflow-hidden bg-secondary-50 dark:bg-secondary-800 flex items-center justify-center">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
            ) : (
              <PhotoIcon className="w-12 h-12 text-secondary-400" />
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={handleLogoChange}
            className="hidden"
            id="new-company-logo-upload"
            data-testid="company-wizard-step1-logo-upload-input"
          />
          <label
            htmlFor="new-company-logo-upload"
            className="cursor-pointer text-sm font-medium text-primary-600 hover:text-primary-500"
            data-testid="company-wizard-step1-logo-upload-label"
          >
            {logoFile ? 'Cambiar imagen' : 'Subir imagen'}
          </label>
        </div>

        <div className="flex-1 space-y-3">
          <Input
            label="Razón social"
            required
            placeholder="Nombre legal de la empresa"
            error={errors.fullName?.message}
            maxLength={150}
            hideCounter
            data-testid="company-wizard-step1-full-name-input"
            {...register('fullName')}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Nombre Comercial"
              required
              placeholder="Nombre comercial o marca"
              error={errors.name?.message}
              maxLength={50}
              hideCounter
              data-testid="company-wizard-step1-name-input"
              {...register('name')}
            />

            <Input
              label="Código"
              required
              placeholder="Código interno de la compañía"
              error={errors.code?.message}
              maxLength={50}
              hideCounter
              data-testid="company-wizard-step1-code-input"
              {...register('code')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Input
                label="RUC"
                required
                placeholder="521-136-113133"
                error={errors.numberId?.message}
                maxLength={25}
                hideCounter
                data-testid="company-wizard-step1-number-id-input"
                {...register('numberId')}
              />
            </div>
            <Input
              label="DV"
              required={isJuridico}
              placeholder="00"
              error={errors.dv?.message}
              maxLength={4}
              hideCounter
              data-testid="company-wizard-step1-dv-input"
              {...register('dv')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select
              label="Tipo de contribuyente"
              required
              error={errors.typeTaxpayer?.message}
              options={[
                { value: '', label: 'Seleccionar tipo de contribuyente...' },
                { value: '1', label: 'Natural' },
                { value: '2', label: 'Jurídico' },
              ]}
              data-testid="company-wizard-step1-type-taxpayer-select"
              {...register('typeTaxpayer')}
            />

            <Input
              label="Teléfono"
              required
              placeholder="0000-0000"
              error={errors.phone?.message}
              maxLength={16}
              hideCounter
              data-testid="company-wizard-step1-phone-input"
              {...register('phone')}
            />
          </div>

          {isSuperAdmin && (
            <Switch
              label="Facturas Ilimitadas"
              checked={isLimited}
              onChange={(checked) => setValue('isLimited', checked, { shouldValidate: true })}
              data-testid="company-wizard-step1-is-limited-switch"
            />
          )}
        </div>
      </div>

      {/*
        Pie con el mismo tratamiento visual que ProductForm (/products):
        sangra hasta el borde del panel con -mx-9/-mb-7 (compensa el px-9
        py-7 de MODAL_BODY). Sin `sticky`: el contenido de este paso es más
        corto que el de Producto, así que un pie sticky quedaba "pegado" al
        fondo del modal dejando un hueco vacío entre el último campo y el
        pie — en flujo normal, el pie sigue directo al contenido.
      */}
      <div className="-mx-9 -mb-7 mt-7 flex flex-wrap items-center justify-end gap-3 border-t border-[#f0f0f0] bg-[#fafafa] px-9 py-[18px] dark:border-secondary-700 dark:bg-secondary-900/95">
        <Button type="submit" variant="primary" className={CTA} data-testid="company-wizard-step1-next-button">
          Siguiente
        </Button>
      </div>
    </form>
  );
}
